import { isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';

import {
  BehaviorSubject,
  Observable, distinctUntilChanged, map, shareReplay,
  switchMap,
  tap,
} from 'rxjs';

import { EmptyURIValue } from '@sondermind/data-access/directory-link';
import { ProviderNetworkCompanyStore } from '@sondermind/data-access/provider-network-company';
import { LocalStorageService } from '@sondermind/local-storage';
import { IInsurance } from '@sondermind/utilities/models-provider-network-company';

import { IsValidStateAbbrev } from '@sondermind/utilities/states-list';
import { IAge } from '../models/age.interface';
import { IGender } from '../models/gender';
import { IModality } from '../models/modality';
import { ISpecialty } from '../models/specialty';
import { ITreatmentPreference } from '../models/treatment-preference.interface';
import { FilterOptionsHttpService } from '../services/filter-options.httpservice';

@Injectable({
  providedIn: 'root'
})
export class FilterOptionsStore {
  genderMap: { [key: number]: string; } = {};
  transformedGenderMap: { [key: number]: string; } = {};

  private readonly genderTransformMap: { [key: string]: string; } = {
    man: 'Male',
    woman: 'Female'
  };

  private genders$!: Observable<IGender[]>;
  private insurances$!: Observable<IInsurance[]>;
  private specialties$!: Observable<ISpecialty[]>;
  private treatmentPreferences$!: Observable<ITreatmentPreference[]>;
  private ages$!: Observable<IAge[]>;
  private modalities$!: Observable<IModality[]>;
  private currentStateSubject = new BehaviorSubject<string | null>(null);
  private currentIsPsychiatristSubject = new BehaviorSubject<boolean>(false);
  private isLoadedSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private isStateLoadedSubject: BehaviorSubject<boolean | null> = new BehaviorSubject<boolean | null>(null);
  private readonly localStorageKey = 'filter-options-current-state';
  private lastFetchedProviderNetworkState: string = '';

  constructor(
    private filtersHttpService: FilterOptionsHttpService,
    private localStorageService: LocalStorageService,
    private insuranceStore: ProviderNetworkCompanyStore,
    @Inject(PLATFORM_ID) private platformId: Object
  ){
    if (isPlatformBrowser(this.platformId)) {
      const storedState = localStorageService.get(this.localStorageKey);
      if (storedState) {
        this.currentStateSubject.next(storedState);
        this.isStateLoadedSubject.next(true);
      } else {
        this.isStateLoadedSubject.next(false);
      }
    }
  }

  setCurrentState(state: string | null): void {
    if (state && state === EmptyURIValue) return;
    if (state && !IsValidStateAbbrev(state)) return;

    this.currentStateSubject.next(state);
    if (state !== null) {
      if (isPlatformBrowser(this.platformId)) {
        this.localStorageService.set(this.localStorageKey, state);
      }
      this.isStateLoadedSubject.next(true);
    } else {
      if (isPlatformBrowser(this.platformId)) {
        this.localStorageService.removeItem(this.localStorageKey);
      }
      this.isStateLoadedSubject.next(false);
    }
  }
  getCurrentState(): string | null {
    return this.currentStateSubject.value;
  }

  setCurrentIsPsychiatrist(isPsychiatrist: boolean): void {
    this.currentIsPsychiatristSubject.next(isPsychiatrist);
  }

  getCurrentIsPsychiatrist(): boolean {
    return this.currentIsPsychiatristSubject.value;
  }

  setIsLoaded(loaded: boolean): void {
    this.isLoadedSubject.next(loaded);
  }

  isLoaded$(): Observable<boolean> {
    return this.isLoadedSubject.asObservable();
  }

  isStateLoaded$(): Observable<boolean | null> {
    return this.isStateLoadedSubject.asObservable();
  }

  currentState$(): Observable<string | null> {
    return this.currentStateSubject.asObservable();
  }

  genderList$(): Observable<IGender[]> {
    if (!this.genders$) {
      this.genders$ = this.filtersHttpService.listGenders$().pipe(
        tap((response) => {
          // Build both maps in a single iteration for better performance
          response.data.forEach(({ id, title }) => {
            this.genderMap[id] = title;
            this.transformedGenderMap[id] = this.genderTransformMap[title.toLowerCase()] || title;
          });
        }),
        map((resp) => resp.data),
        shareReplay(1)
      );
    }

    return this.genders$;
  }

  insuranceList$(): Observable<IInsurance[]> {
    return this.currentStateSubject.pipe(
      distinctUntilChanged(),
      switchMap((state) => {
        if (state && (!this.insurances$ || this.lastFetchedProviderNetworkState !== state)) {
          this.lastFetchedProviderNetworkState = state;
          this.insurances$ = this.insuranceStore.getInsuranceOptionsByState$({ state });
        }

        // Filter insurances based on Psychiatric & Med Mgmt filter
        const isPsychiatrist = this.getCurrentIsPsychiatrist();
        return this.insurances$.pipe(
          map((insurances) => (isPsychiatrist ? insurances.filter((insurance) => insurance.includesPsych) : insurances))
        );
      })
    );
  }

  specialtyList$(): Observable<ISpecialty[]> {
    if (!this.specialties$) {
      this.specialties$ = this.filtersHttpService.listSpecialties$({ page: 'disabled' }).pipe(
        map((resp) => resp.data),
        shareReplay(1)
      );
    }

    return this.specialties$;
  }

  modalityList$(): Observable<IModality[]> {
    if (!this.modalities$) {
      this.modalities$ = this.filtersHttpService.listModalities$().pipe(
        map((resp) => resp.data),
        shareReplay(1)
      );
    }

    return this.modalities$;
  }

  treatmentPreferenceList$(): Observable<ITreatmentPreference[]> {
    if (!this.treatmentPreferences$) {
      this.treatmentPreferences$ = this.filtersHttpService.listTreatmentPreferences$({ page: 'disabled' }).pipe(
        map((resp) => resp.data),
        shareReplay(1)
      );
    }

    return this.treatmentPreferences$;
  }

  ageList$(): Observable<IAge[]> {
    if (!this.ages$) {
      this.ages$ = this.filtersHttpService.listAges$({ page: 'disabled' }).pipe(
        map((resp) => resp.data),
        shareReplay(1)
      );
    }

    return this.ages$;
  }
}
